import { css, html, nothing } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import SignaturePad from "signature_pad";
import { Dialog } from "../../elements/dialog";
import "./contact-info-form";
import type { ContactInfoForm } from "./contact-info-form";

export interface SignatureDialogResult {
    src: string;
    info?: {
        firstName: string;
        lastName: string;
        address: string;
        email: string;
        phone: string;
    };
}

@customElement("ptc-signature-dialog")
export class SignatureDialog extends Dialog<void, SignatureDialogResult> {
    @query("canvas")
    private _canvas: HTMLCanvasElement;

    @property({ type: Boolean })
    public requestContactInfo = false;

    @query(".canvas-wrapper")
    private _canvasWrapper: HTMLElement;

    private _pad: SignaturePad;

    @state()
    private _hasSignature = false;

    readonly preventDismiss = true;

    firstUpdated() {
        this._pad = new SignaturePad(this._canvas);
        this._pad.addEventListener("endStroke", () => {
            this._hasSignature = !this._pad.isEmpty();
        });
    }

    async show() {
        const promise = super.show();

        await this.updateComplete;

        const dpr = window.devicePixelRatio || 0;
        this._canvas.width = this._canvasWrapper.offsetWidth * dpr;
        this._canvas.height = this._canvasWrapper.offsetHeight * dpr;
        this._canvas.getContext("2d")!.scale(dpr, dpr);
        this._pad.clear();
        this._hasSignature = false;

        return promise;
    }

    static styles = [
        ...Dialog.styles,
        css`
            .canvas-wrapper {
                position: relative;
                padding-bottom: 50%;
            }

            canvas {
                top: 0;
                left: 0;
                position: absolute;
                width: 100%;
                height: 100%;
            }
        `,
    ];

    @query("ptc-contact-info-form")
    private _contactInfoForm?: ContactInfoForm;

    private _clearPad() {
        this._pad.clear();
        this._hasSignature = false;
    }

    private _completeDialog() {
        if (this._pad.isEmpty()) {
            return;
        }
        const src = this._canvas.toDataURL();
        const info = (this.requestContactInfo && this._contactInfoForm?.info) || undefined;

        if (info || !this.requestContactInfo) {
            this.done({ src, info });
        }
    }

    renderContent() {
        return html`
            <div class="padded vertical layout">
                ${this.requestContactInfo
                    ? html`<div class="border-bottom"><ptc-contact-info-form></ptc-contact-info-form></div>`
                    : nothing}
                <div class="canvas-wrapper">
                    <canvas></canvas>
                </div>
                <div class="padded subtle border-top text-centering">Hier Unterschreiben</div>
            </div>
            <div class="padded evenly stretching spacing horizontal layout">
                <button class="primary" @click=${() => this._completeDialog()} ?disabled=${!this._hasSignature}>
                    Übernehmen
                </button>
                <button class="transparent" @click=${() => this.done()}>Abbrechen</button>
                <button class="transparent" @click=${() => this._clearPad()}>Zurücksetzen</button>
            </div>
        `;
    }
}
